/**
 * services/sync/providerFactory.ts
 * Factory for creating sync provider instances
 *
 * Registry pattern: Extensible map-based provider instantiation
 * DI pattern: Keeps SyncInitializationService testable and import-cycle free
 * Exported as namespace for clean test spies: vi.spyOn(providerFactory, 'createProvider')
 */

import { GoogleDriveSyncProvider } from './GoogleDriveSyncProvider';
import { LocalDirectorySyncProvider } from './LocalDirectorySyncProvider';
import type { SyncProvider } from '@/types/sync/SyncProvider';
import type { UserSyncSettings } from '@/types/supabase/UserSyncSettings';

// ===== TYPE DEFINITIONS =====

/**
 * Provider key type (non-nullable activeSyncProvider)
 */
type ProviderKey = NonNullable<UserSyncSettings['activeSyncProvider']>;

/**
 * Provider constructor signature
 */
type ProviderCtor = new () => SyncProvider;

// ===== REGISTRY =====

/**
 * Provider registry map (PRIVATE)
 * Extensible for future providers (OneDrive, Dropbox, Box)
 */
const registry: Partial<Record<ProviderKey, ProviderCtor>> = {
  'google-drive': GoogleDriveSyncProvider,
  local: LocalDirectorySyncProvider,
  // Future providers:
  // 'onedrive': OneDriveSyncProvider,
  // 'dropbox': DropboxSyncProvider,
  // 'box': BoxSyncProvider,
};

// ===== FACTORY FUNCTION =====

/**
 * Create sync provider instance based on user settings
 *
 * Registry pattern for provider instantiation:
 * - Map-based lookup (extensible for future providers)
 * - Synchronous construction (no hidden async)
 * - Centralizes provider creation logic
 * - Enables clean mocking in tests (import * as providerFactory)
 * - Avoids circular dependencies between service and providers
 *
 * @param kind - Provider type from user settings
 * @returns Provider instance or null if unknown/unsupported
 *
 * @example
 * ```ts
 * const provider = createProvider('google-drive');
 * if (provider) {
 *   const connected = await provider.isConnected();
 * }
 * ```
 *
 * @example Test mocking
 * ```ts
 * import * as providerFactory from './providerFactory';
 * vi.spyOn(providerFactory, 'createProvider').mockReturnValue(mockProvider);
 * ```
 */
export function createProvider(
  kind: UserSyncSettings['activeSyncProvider']
): SyncProvider | null {
  if (!kind) {
    return null;
  }

  const Ctor = registry[kind as ProviderKey];

  if (!Ctor) {
    console.warn(`Unknown provider: ${kind}`);
    return null;
  }

  return new Ctor();
}
